/**
 * operations-industrie.ts
 * ─────────────────────────────────────────────────────────────────
 * Configuration centralisée des pages "stubs" /industrie/operations/[slug].
 *
 * 7 opérations pour les utilités et le bâti industriel + 1 page sur l'audit
 * énergétique réglementaire.
 */

import type { ParcoursStub } from './parcours-particuliers';

export const OPERATIONS_INDUSTRIE: ParcoursStub[] = [
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'variateurs',
    iconName: 'zap',
    eyebrow: 'Fiche IND-UT-114',
    title: 'Variateurs électroniques de vitesse',
    shortLabel: 'Variateurs',
    lead: "Les variateurs ajustent la vitesse de vos moteurs au besoin réel, au lieu de les faire tourner à plein régime. Votre facture d'électricité baisse directement.",
    badge: 'IND-UT-114',
    badgeLabel: 'Secteur Industrie, utilités',
    keyPoints: [
      {
        iconName: 'zap',
        label: 'Pompes & ventilateurs',
        description: "Vos pompes et ventilateurs à charge variable sont les mieux placés. L'économie est immédiate.",
      },
      {
        iconName: 'cog',
        label: 'Adaptation automatique',
        description: "Le moteur module sa vitesse automatiquement. Vous payez uniquement l'énergie dont vous avez besoin.",
      },
      {
        iconName: 'file-text',
        label: 'Prime IND-UT-114',
        description: "Prime basée sur la puissance du moteur et son temps d'utilisation. Plusieurs configurations possibles.",
      },
      {
        iconName: 'gauge',
        label: 'Mesurez vos gains',
        description: "Suivez vos économies réelles et ajustez vos réglages en continu.",
      },
    ],
    steps: [
      {
        title: 'Identifier les moteurs prioritaires',
        description: "Repérez vos moteurs de pompes et ventilateurs à charge variable. Priorité à ceux qui tournent le plus d'heures par an.",
      },
      {
        title: "Chiffrage et devis",
        description: "Dimensionnement du variateur, vérification de la compatibilité moteur, devis pour une armoire neuve ou un rétrofit.",
      },
      {
        title: "Débloquez la prime avant le devis",
        description: "La demande de prime doit être faite avant la signature du devis. On vous guide sur les pièces à fournir.",
      },
      {
        title: "Installation et réglages",
        description: "Pose du variateur, paramétrage et vérification. Formation de vos équipes.",
      },
      {
        title: "Suivi des consommations",
        description: "Mesurez vos économies, calculez votre retour sur investissement et pilotez vos indicateurs.",
      },
    ],
    faq: [
      {
        question: "Tous les moteurs sont-ils candidats à un variateur ?",
        answer:
          "Surtout les moteurs à charge variable (pompes, ventilateurs). Sur charge constante, le gain est limité. On identifie vos priorités.",
      },
      {
        question: "Quelle durée de vie pour un variateur ?",
        answer:
          "7 à 15 ans en conditions normales, avec entretien régulier (remplacement périodique des condensateurs et ventilateurs).",
      },
      {
        question: "Quels prérequis électriques ?",
        answer:
          "Qualité d'alimentation, câblage moteur compatible et bonne mise à la terre. Un filtre peut être nécessaire si le câble est long.",
      },
    ],
    ctaLabel: 'Identifier mes lignes éligibles',
    ctaHref: '/industrie/diagnostic-cee',
    secondaryCta: { label: 'Voir la fiche officielle (IND-UT-114)', href: '/cee/fiches?secteur=IND' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'moteurs-haute-efficacite',
    iconName: 'cog',
    eyebrow: 'Fiche IND-UT-102',
    title: 'Moteurs haute efficacité IE3 / IE4',
    shortLabel: 'Moteurs HE',
    lead: "Remplacer un vieux moteur par un modèle haut rendement (IE3 ou IE4) réduit directement votre facture. Une prime est disponible.",
    badge: 'IND-UT-102',
    badgeLabel: 'Secteur Industrie, utilités',
    keyPoints: [
      {
        iconName: 'cog',
        label: 'IE3 ou IE4',
        description: "IE3, le standard des équipements neufs. IE4, rendement supérieur pour les longues durées d'usage.",
      },
      {
        iconName: 'zap',
        label: "Moins de pertes",
        description: "Meilleurs matériaux, moins de frottements internes : le rendement grimpe, votre facture baisse.",
      },
      {
        iconName: 'file-text',
        label: 'Prime IND-UT-102',
        description: "Prime basée sur la puissance et le temps d'utilisation annuel. Versée pour le remplacement d'un moteur existant.",
      },
      {
        iconName: 'shield-check',
        label: 'Couplage avec variateur',
        description: "Moteur performant + variateur = gains cumulés. Les deux primes sont cumulables.",
      },
    ],
    steps: [
      {
        title: 'Repérez vos moteurs à remplacer',
        description: "Listez vos moteurs anciens qui tournent beaucoup : priorité aux IE1 ou IE2 pour maximiser le gain.",
      },
      {
        title: 'Spécifications et devis',
        description: "IE3 ou IE4 selon vos besoins, vérification de la compatibilité mécanique, devis fournisseur.",
      },
      {
        title: "Débloquez la prime avant le devis",
        description: "La demande de prime doit être faite avant la signature du devis. On vous assiste pour le dossier.",
      },
      {
        title: "Remplacement et mise en service",
        description: "Dépose de l'ancien moteur, pose du nouveau, contrôles électriques et mécaniques.",
      },
      {
        title: "Suivez vos économies",
        description: "Mesurez périodiquement la consommation pour valider le retour sur investissement.",
      },
    ],
    faq: [
      {
        question: "Faut-il toujours remplacer un moteur IE2 en service ?",
        answer:
          "Uniquement s'il tourne beaucoup d'heures par an. Sur un moteur peu sollicité, le retour est trop long. On vous aide à prioriser.",
      },
      {
        question: "IE3 ou IE4 : lequel choisir ?",
        answer:
          "IE4 a un meilleur rendement mais coûte plus cher. Rentable pour les moteurs 24h/24. IE3 convient pour les usages standards.",
      },
      {
        question: "Le nouveau moteur est-il compatible avec mon variateur ?",
        answer:
          "Généralement oui. Vérifiez les préconisations du constructeur et la cohérence puissance moteur/variateur.",
      },
    ],
    ctaLabel: 'Identifier mes moteurs candidats',
    ctaHref: '/industrie/diagnostic-cee',
    secondaryCta: { label: 'Voir la fiche officielle (IND-UT-102)', href: '/cee/fiches?secteur=IND' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'air-comprime',
    iconName: 'wind',
    eyebrow: 'Fiche IND-UT-117',
    title: "Optimisation production d'air comprimé",
    shortLabel: 'Air comprimé',
    lead: "L'air comprimé pèse souvent 10 à 20 % de votre facture. Réparer les fuites, baisser la pression et récupérer la chaleur sont des actions simples et très rentables.",
    badge: 'IND-UT-117',
    badgeLabel: 'Secteur Industrie, utilités',
    keyPoints: [
      {
        iconName: 'wind',
        label: "Colmatez les fuites d'abord",
        description: "20 à 30 % de l'air produit disparaît en fuites sur un réseau non contrôlé. Une campagne de détection = gains immédiats.",
      },
      {
        iconName: 'gauge',
        label: 'Baissez la pression',
        description: "Passer de 7 à 6 bars suffit. Moins de pression = moins d'électricité consommée.",
      },
      {
        iconName: 'flame',
        label: 'Récupérez la chaleur',
        description: "Jusqu'à 80 % de l'énergie du compresseur est récupérable en chaleur, pour chauffer vos locaux ou votre eau.",
      },
      {
        iconName: 'cog',
        label: 'Pilotez vos compresseurs',
        description: "Coordonnez plusieurs compresseurs pour éviter la marche à vide et ajuster la production à la demande réelle.",
      },
    ],
    steps: [
      {
        title: 'Auditez votre réseau',
        description: "Mesurez la pression, détectez les fuites, analysez votre profil de consommation. Audit en 1 à 3 jours.",
      },
      {
        title: "Priorisez les actions",
        description: "D'abord les fuites (gain rapide), puis la pression, le variateur et enfin la récupération de chaleur.",
      },
      {
        title: "Débloquez les primes avant travaux",
        description: "Pour les actions éligibles, la demande de prime se fait avant la signature du devis.",
      },
      {
        title: "Travaux coordonnés",
        description: "Interventions planifiées pendant vos arrêts de production : étanchéité, réglages, récupération de chaleur.",
      },
      {
        title: "Suivi permanent",
        description: "Gardez une instrumentation en place et programmez une détection de fuites annuelle.",
      },
    ],
    faq: [
      {
        question: "Quel gain attendre d'un programme air comprimé ?",
        answer:
          "Très variable selon l'état de votre réseau. Sur un site jamais audité, le cumul fuites + pression + récupération réduit fortement la facture. On chiffre votre potentiel.",
      },
      {
        question: "Comment détecter les fuites ?",
        answer:
          "Par détection ultrasonore en production. Une campagne complète dure 1 à 3 jours : chaque fuite est localisée et priorisée.",
      },
      {
        question: "La récupération de chaleur est-elle toujours utile ?",
        answer:
          "Uniquement si vous avez un besoin de chaleur en même temps que le compresseur tourne. On évalue au cas par cas.",
      },
    ],
    ctaLabel: 'Auditer mon réseau air comprimé',
    ctaHref: '/industrie/diagnostic-cee',
    secondaryCta: { label: 'Voir la fiche officielle (IND-UT-117)', href: '/cee/fiches?secteur=IND' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'recuperation-chaleur',
    iconName: 'flame',
    eyebrow: 'Fiche IND-UT-131',
    title: 'Récupération de chaleur fatale sur process',
    shortLabel: 'Chaleur fatale',
    lead: "Vos procédés rejettent de la chaleur réutilisable : fumées, condensats, vapeur basse pression. Une prime existe pour financer cette valorisation.",
    badge: 'IND-UT-131',
    badgeLabel: 'Secteur Industrie, utilités',
    keyPoints: [
      {
        iconName: 'flame',
        label: 'Vos sources de chaleur perdue',
        description: "Fumées de chaudière ou de four, air de refoulement des compresseurs, eau de refroidissement, vapeur basse pression non utilisée.",
      },
      {
        iconName: 'thermometer',
        label: 'Un échangeur adapté',
        description: "Plaques, tubulaire ou caloduc selon la température et la nature des fluides. Dimensionné pour votre couple source/besoin.",
      },
      {
        iconName: 'cog',
        label: 'Réutilisez la chaleur',
        description: "Préchauffez l'air de combustion, l'eau process ou l'eau chaude sanitaire de vos bâtiments.",
      },
      {
        iconName: 'sparkles',
        label: 'Bonus possible',
        description: "Une bonification peut augmenter le montant de la prime de base pour cette opération.",
      },
    ],
    steps: [
      {
        title: 'Cartographiez vos rejets thermiques',
        description: "Recensez toutes vos sources de chaleur perdue : température, débit, durée. Identifiez les besoins de chaleur à proximité.",
      },
      {
        title: 'Étudiez la faisabilité',
        description: "Dimensionnement de l'échangeur, simulation des économies, chiffrage et estimation du retour sur investissement.",
      },
      {
        title: 'Financement',
        description: "Prime débloquée avant signature du devis. Cumul possible avec les aides à la chaleur renouvelable.",
      },
      {
        title: 'Installation',
        description: "Pose de l'échangeur, raccordements, pilotage. Mise en service par un professionnel qualifié.",
      },
      {
        title: 'Suivez la performance',
        description: "Mesurez l'énergie récupérée, planifiez l'entretien régulier pour maintenir l'efficacité.",
      },
    ],
    faq: [
      {
        question: "Toute chaleur fatale est-elle récupérable ?",
        answer:
          "Non. Il faut une température suffisante, une synchronisation production/besoin et une distance raisonnable. L'étude de faisabilité tranche.",
      },
      {
        question: "Quel rendement pour un échangeur ?",
        answer:
          "Un échangeur à plaques sur fluides propres peut atteindre un très bon rendement. Sur fumées chargées, prévoyez une marge et un nettoyage régulier.",
      },
      {
        question: "Peut-on vendre la chaleur à l'extérieur ?",
        answer:
          "Oui, vous pouvez céder votre chaleur à un réseau voisin. Les délais sont plus longs qu'une valorisation interne.",
      },
    ],
    ctaLabel: 'Évaluer mon gisement de chaleur fatale',
    ctaHref: '/industrie/diagnostic-cee',
    secondaryCta: { label: 'Voir la fiche officielle (IND-UT-131)', href: '/cee/fiches?secteur=IND' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'calorifugeage',
    iconName: 'shield-check',
    eyebrow: 'Fiche IND-UT-103',
    title: 'Isolation des points singuliers (calorifugeage)',
    shortLabel: 'Calorifugeage',
    lead: "Vos vannes et brides non isolées gaspillent l'équivalent de plusieurs mètres de tuyauterie. Des matelas démontables stoppent ces pertes.",
    badge: 'IND-UT-103',
    badgeLabel: 'Secteur Industrie, utilités',
    keyPoints: [
      {
        iconName: 'shield-check',
        label: 'Des pertes concentrées',
        description: "Une vanne non isolée perd l'équivalent de plusieurs mètres de tuyauterie. Sur un réseau vapeur permanent, l'impact est lourd.",
      },
      {
        iconName: 'cog',
        label: 'Matelas démontables sur mesure',
        description: "Isolants souples adaptés à chaque vanne ou bride. Démontables pour la maintenance, puis remis en place.",
      },
      {
        iconName: 'file-text',
        label: 'Prime IND-UT-103',
        description: "Prime basée sur le type de point à isoler, la température de service et la durée de fonctionnement.",
      },
      {
        iconName: 'sparkles',
        label: 'Bonus possible',
        description: "Une bonification peut augmenter la prime de base pour le calorifugeage des points singuliers.",
      },
    ],
    steps: [
      {
        title: 'Audit thermographique',
        description: "Une caméra thermique cartographie tous vos points chauds non isolés : vapeur, eau chaude, fluides process.",
      },
      {
        title: 'Chiffrez les pertes',
        description: "Pour chaque point : calculez les pertes annuelles et priorisez par gain potentiel.",
      },
      {
        title: 'Débloquez la prime',
        description: "Demande de prime avant signature du devis. Photos avant/après à prévoir.",
      },
      {
        title: 'Pose des matelas',
        description: "Fabrication sur mesure, pose par un calorifugeur qualifié, vérification de l'absence de ponts thermiques.",
      },
      {
        title: 'Contrôle et suivi',
        description: "Nouvelle thermographie pour valider la baisse de température. Calculez vos économies réelles.",
      },
    ],
    faq: [
      {
        question: "À partir de quelle température est-ce rentable ?",
        answer:
          "Au-delà de 60-70 °C en fonctionnement continu. Plus la température est élevée, plus le retour est rapide.",
      },
      {
        question: "Matelas démontable ou isolation classique ?",
        answer:
          "Le matelas démontable est plus cher mais évite de détruire l'isolant à chaque maintenance. Plus économique sur la durée.",
      },
      {
        question: "Peut-on isoler en zone à risque ?",
        answer:
          "Oui, avec des matériaux adaptés et un calorifugeur qualifié pour ce type d'intervention.",
      },
    ],
    ctaLabel: 'Auditer mes points singuliers',
    ctaHref: '/industrie/diagnostic-cee',
    secondaryCta: { label: 'Voir la fiche officielle (IND-UT-103)', href: '/cee/fiches?secteur=IND' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'chaufferie',
    iconName: 'flame',
    eyebrow: 'Fiche IND-UT-115',
    title: 'Optimisation chaufferie process / brûleur HR',
    shortLabel: 'Chaufferie',
    lead: "Remplacer un vieux brûleur par un modèle modulant à haut rendement, installer un économiseur sur les fumées et mieux piloter la chaufferie : trois leviers pour réduire votre facture.",
    badge: 'IND-UT-115',
    badgeLabel: 'Secteur Industrie, utilités',
    keyPoints: [
      {
        iconName: 'flame',
        label: 'Brûleur modulant intelligent',
        description: "La puissance s'ajuste en continu selon la demande, au lieu du tout-ou-rien. La combustion est optimisée en permanence.",
      },
      {
        iconName: 'thermometer',
        label: 'Économiseur sur fumées',
        description: "Récupérez la chaleur des fumées pour préchauffer l'eau d'alimentation. Le rendement de la chaudière grimpe de plusieurs points.",
      },
      {
        iconName: 'cog',
        label: 'Pilotage global',
        description: "Coordonnez vos chaudières, séquencez les démarrages, ajustez la production à la courbe de charge réelle.",
      },
      {
        iconName: 'file-text',
        label: 'Prime IND-UT-115',
        description: "Prime basée sur la puissance de la chaudière et le combustible. Plusieurs configurations possibles.",
      },
    ],
    steps: [
      {
        title: 'Diagnostic combustion',
        description: "Mesurez la composition des fumées, la température et le rendement actuel. Analysez votre profil de charge annuel.",
      },
      {
        title: 'Préconisations chiffrées',
        description: "Choix du brûleur adapté, dimensionnement d'un éventuel économiseur, plan de pilotage si plusieurs chaudières.",
      },
      {
        title: 'Financement',
        description: "Prime débloquée avant signature du devis. Cumul possible avec les aides à la chaleur renouvelable.",
      },
      {
        title: 'Mise en œuvre',
        description: "Remplacement du brûleur, pose de l'économiseur, raccordement de la régulation, réglages, formation de vos équipes.",
      },
      {
        title: 'Suivi du rendement',
        description: "Mesurez périodiquement le rendement, suivez vos consommations, planifiez l'entretien préventif.",
      },
    ],
    faq: [
      {
        question: "Quel gain attendre d'un brûleur HR + économiseur ?",
        answer:
          "Variable selon votre installation. Sur une vieille chaudière tout-ou-rien, le gain peut atteindre plusieurs points de rendement. Notre diagnostic chiffre votre potentiel.",
      },
      {
        question: "L'économiseur fonctionne-t-il avec tous les combustibles ?",
        answer:
          "Oui, avec des contraintes différentes. Le gaz ne pose pas de problème de condensation acide. Fioul ou biomasse nécessitent des matériaux résistants.",
      },
      {
        question: "Brûleur HR ou passage à la biomasse ?",
        answer:
          "Le brûleur HR optimise votre combustible actuel. La biomasse change le combustible et nécessite une nouvelle chaudière. Deux logiques cumulables.",
      },
    ],
    ctaLabel: 'Diagnostiquer ma chaufferie',
    ctaHref: '/industrie/diagnostic-cee',
    secondaryCta: { label: 'Voir la fiche officielle (IND-UT-115)', href: '/cee/fiches?secteur=IND' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'eclairage',
    iconName: 'sparkles',
    eyebrow: 'Fiche BAT-EQ-127',
    title: 'Éclairage industriel LED haute efficacité',
    shortLabel: 'Éclairage LED',
    lead: "Remplacez vos tubes fluorescents et lampes à décharge par des LED performantes : moins de consommation, meilleure lumière dans vos ateliers et bureaux.",
    badge: 'BAT-EQ-127',
    badgeLabel: 'Applicable au bâti industriel',
    keyPoints: [
      {
        iconName: 'sparkles',
        label: 'LED haute efficacité',
        description: "Des luminaires qui consomment nettement moins et durent beaucoup plus longtemps que la fluorescence ou les lampes à décharge.",
      },
      {
        iconName: 'gauge',
        label: 'Gradation et détection',
        description: "La lumière s'adapte automatiquement : détection de présence, luminosité naturelle, scénarios par zone.",
      },
      {
        iconName: 'shield-check',
        label: 'Meilleure qualité de lumière',
        description: "Bon rendu des couleurs, température adaptée au poste, moins d'éblouissement : confort et productivité améliorés.",
      },
      {
        iconName: 'file-text',
        label: 'Prime BAT-EQ-127',
        description: "Prime basée sur le nombre de luminaires, leur puissance et le temps d'utilisation annuel.",
      },
    ],
    steps: [
      {
        title: 'Audit de votre éclairage',
        description: "Inventaire des luminaires, mesure des niveaux d'éclairement par poste, repérage des zones à améliorer.",
      },
      {
        title: 'Plan de remplacement',
        description: "Choix des LED par zone (atelier, stockage, bureaux), définition du pilotage, respect des normes d'éclairement.",
      },
      {
        title: 'Débloquez la prime',
        description: "Demande de prime avant signature du devis. Caractéristiques des luminaires et factures à fournir.",
      },
      {
        title: 'Pose et mise en service',
        description: "Dépose des anciens luminaires, pose des LED, paramétrage de la détection et gradation.",
      },
      {
        title: 'Suivi de la consommation',
        description: "Compteur dédié si possible, calcul des économies réelles, suivi annuel de la performance.",
      },
    ],
    faq: [
      {
        question: "Quelle durée de vie pour un luminaire LED industriel ?",
        answer:
          "Plusieurs dizaines de milliers d'heures, soit des années en service intensif. La durée réelle dépend de la qualité du driver et des conditions thermiques.",
      },
      {
        question: "Le LED supporte-t-il la chaleur ?",
        answer:
          "Les LED n'aiment pas les températures élevées. En environnement chaud, choisissez des luminaires industriels conçus pour (driver déporté, dissipation renforcée).",
      },
      {
        question: "Et l'éclairage de sécurité ?",
        answer:
          "L'éclairage de sécurité suit des règles spécifiques et n'est pas concerné par cette prime. Profitez du chantier pour vérifier sa conformité.",
      },
    ],
    ctaLabel: 'Auditer mon éclairage industriel',
    ctaHref: '/industrie/diagnostic-cee',
    secondaryCta: { label: 'Voir la fiche officielle (BAT-EQ-127)', href: '/cee/fiches?secteur=BAT' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'audit-reglementaire',
    iconName: 'file-search',
    eyebrow: 'Décret 2014-1393',
    title: 'Audit énergétique réglementaire',
    shortLabel: 'Audit réglementaire',
    lead: "Certaines grandes entreprises doivent réaliser un audit énergétique tous les 4 ans. Cette obligation est distincte du dispositif de primes.",
    badge: 'Tous les 4 ans',
    badgeLabel: "Obligation pour grandes entreprises",
    keyPoints: [
      {
        iconName: 'file-search',
        label: 'Qui est concerné',
        description: "Entreprises remplissant au moins 2 critères sur 3 : ≥ 250 salariés, ≥ 50 M€ de chiffre d'affaires, ≥ 43 M€ de bilan. Couvre 80 % des consommations.",
      },
      {
        iconName: 'gauge',
        label: 'Audit normé',
        description: "Un cadre méthodologique précis : volet général (bâtiments, transports, process) et volet approfondi pour l'industrie.",
      },
      {
        iconName: 'shield-check',
        label: 'Dispense possible',
        description: "Une certification de management de l'énergie couvrant au moins 80 % des consommations peut vous dispenser d'audit.",
      },
      {
        iconName: 'gavel',
        label: 'Contrôles et sanctions',
        description: "Non-respect : amende pouvant atteindre 2 % du chiffre d'affaires annuel, 4 % en cas de récidive.",
      },
    ],
    steps: [
      {
        title: 'Vérifiez votre obligation',
        description: "Effectif, chiffre d'affaires, bilan : déterminez si vous êtes concerné et définissez le périmètre à couvrir.",
      },
      {
        title: 'Choisissez un auditeur',
        description: "Bureau d'études certifié pour ce type d'audit. Possibilité d'auditeur interne si votre entreprise est certifiée.",
      },
      {
        title: 'Audit terrain et analyse',
        description: "Visite des sites, collecte des données de consommation, identification des pistes d'amélioration.",
      },
      {
        title: "Rapport d'audit",
        description: "Rapport avec le périmètre, la méthodologie, les résultats et des recommandations chiffrées.",
      },
      {
        title: 'Dépôt officiel',
        description: "Dépôt obligatoire du rapport. Conservation 9 ans. Renouvellement tous les 4 ans.",
      },
    ],
    faq: [
      {
        question: "Mon entreprise est-elle concernée ?",
        answer:
          "Oui si vous remplissez au moins 2 des 3 critères : effectif ≥ 250, chiffre d'affaires ≥ 50 M€, ou bilan ≥ 43 M€.",
      },
      {
        question: "Quelles conséquences en cas de non-audit ?",
        answer:
          "Mise en demeure puis amende pouvant atteindre 2 % du chiffre d'affaires, 4 % en récidive. Les contrôles se sont intensifiés.",
      },
      {
        question: "Quel est le coût d'un audit ?",
        answer:
          "Le coût dépend du nombre de sites et de la complexité. Des diagnostics préparatoires peuvent être aidés selon votre région.",
      },
    ],
    ctaLabel: "Vérifier mon obligation d'audit",
    ctaHref: '/contact?source=industrie&besoin=audit-reglementaire',
    secondaryCta: { label: 'En savoir plus sur la certification énergie', href: '/industrie/iso-50001' },
  },
];

export function getOperationIndustrieStub(slug: string): ParcoursStub | undefined {
  return OPERATIONS_INDUSTRIE.find((o) => o.slug === slug);
}
